import ReactSelect, { GroupBase, Props as SelectProps } from 'react-select';

interface CustomSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends SelectProps<Option, IsMulti, Group> {
  isError?: boolean;
}

const customSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    boxShadow: state.isFocused
      ? '0 0 0 2px rgba(194, 65, 12, 0.18)'
      : 'none',
    fontSize: '14px',
    fontFamily: '"DM Sans", sans-serif',
    fontWeight: '400',
    lineHeight: '20px',
    letterSpacing: '0.01em',
    backgroundColor: '#fff',
    minHeight: '44px',
    height: state.selectProps.isMulti ? 'auto' : '44px',
    alignItems: state.selectProps.isMulti ? 'flex-start' : 'center',
    '&:hover': {
      border: '1px solid #d6d3d1',
    },
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: '#6b7280',
    opacity: 1,
    fontSize: '14px',
    fontFamily: '"DM Sans", sans-serif',
    fontWeight: '400',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: '#1a1a1a',
    fontSize: '14px',
    fontFamily: '"DM Sans", sans-serif',
    fontWeight: '400',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    color: '#6b7280',
    padding: '0 6px',
    '&:hover': {
      color: '#374151',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: '2px 8px',
    minHeight: '36px',
    height: 'auto',
    flexWrap: 'wrap',
    overflow: 'visible',
  }),
  multiValue: (provided: any) => ({
    ...provided,
    maxWidth: '100%',
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    whiteSpace: 'normal',
  }),
  input: (provided: any) => ({
    ...provided,
    margin: '0',
    padding: '0',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#c2410c'
      : state.isFocused
        ? '#fff7ed'
        : '#fff',
    color: state.isSelected ? '#fff' : '#1f2937',
    fontSize: '13px',
    fontFamily: '"DM Sans", sans-serif',
    fontWeight: '500',
    padding: '10px 10px',
    borderRadius: '8px',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: state.isSelected ? '#9a3412' : '#ffedd5',
    },
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: '0',
    maxHeight: '240px',
  }),
  menu: (provided: any) => ({
    ...provided,
    marginTop: '4px',
    padding: '4px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
    zIndex: 9999,
  }),
};

function Select<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ isError, ...props }: CustomSelectProps<Option, IsMulti, Group>) {
  const styles = isError
    ? {
        ...customSelectStyles,
        control: (provided: any, state: any) => ({
          ...customSelectStyles.control(provided, state),
          border: '2px solid #e53e3e',
          boxShadow: state.isFocused
            ? '0 0 0 2px rgba(229, 62, 62, 0.18)'
            : 'none',
          '&:hover': { border: '2px solid #e53e3e' },
        }),
      }
    : customSelectStyles;

  return <ReactSelect {...props} styles={styles as any} />;
}

export default Select;
